import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import { useConfirmModal } from '../../hooks';
import { useNotifications, ConfirmModal } from '../common';
import { InfoIcon } from '@phosphor-icons/react';
import './lists.css';

interface PastGiftsBannerProps {
  personId: number;
  eventId: number;
  onGiftsCopied: () => void;
}

const PastGiftsBanner: React.FC<PastGiftsBannerProps> = ({
  personId,
  eventId,
  onGiftsCopied,
}) => {
  const [shouldShow, setShouldShow] = useState(false);
  const [giftCount, setGiftCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { isOpen, config, isLoading: isConfirmLoading, showConfirm, handleConfirm, handleCancel } = useConfirmModal();
  const { showSuccess } = useNotifications();

  useEffect(() => {
    checkPastGifts();
  }, [personId, eventId]);

  const checkPastGifts = async () => {
    try {
      const result = await apiService.checkPastUnreservedGifts(eventId, personId);
      setShouldShow(result.shouldShowBanner);
      setGiftCount(result.count);
    } catch (err) {
      console.error('Error checking past gifts:', err);
    }
  };

  const handleYes = async () => {
    setIsLoading(true);
    try {
      const result = await apiService.copyPastUnreservedGifts(eventId, personId);
      setShouldShow(false);
      showSuccess(
        'Regali aggiunti!',
        `${result.copiedCount} ${result.copiedCount === 1 ? 'regalo è stato aggiunto' : 'regali sono stati aggiunti'} alla tua lista.`
      );
      onGiftsCopied();
    } catch (err) {
      console.error('Error copying past gifts:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNo = async () => {
    const confirmed = await showConfirm({
      title: 'Attenzione',
      message: 'Non sarà più possibile recuperare i regali passati, sei sicuro?',
      confirmText: 'Sì, sono sicuro',
      cancelText: 'Annulla',
      confirmVariant: 'danger',
    });

    if (confirmed) {
      setIsLoading(true);
      try {
        await apiService.declinePastGiftsCopy(eventId, personId);
        setShouldShow(false);
      } catch (err) {
        console.error('Error declining past gifts:', err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  if (!shouldShow) {
    return null;
  }

  return (
    <>
      <div className="past-gifts-banner">
        <div className="past-gifts-banner-content">
          <InfoIcon size={20} weight="fill" />
          <p>
            Sono stati trovati <strong>{giftCount}</strong> regali non prenotati in eventi passati.
            Vuoi aggiungerli a questa lista?
          </p>
        </div>
        <div className="past-gifts-banner-actions">
          <button
            onClick={handleYes}
            disabled={isLoading}
            className="action-button primary"
          >
            Sì, aggiungi
          </button>
          <button
            onClick={handleNo}
            disabled={isLoading}
            className="action-button secondary"
          >
            No, grazie
          </button>
        </div>
      </div>

      {/* Confirm Modal */}
      {config && (
        <ConfirmModal
          isOpen={isOpen}
          title={config.title}
          message={config.message}
          confirmText={config.confirmText}
          cancelText={config.cancelText}
          confirmVariant={config.confirmVariant}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
          isLoading={isConfirmLoading}
        />
      )}
    </>
  );
};

export default PastGiftsBanner;
